<template>
  <div
    v-if="travelerPictures?.length > 0"
    class="flex flex-col"
  >
    <ScreenWidthProvider v-slot="{ isWider }">
      <div class="flex mb-4">
        <div
          class="text-2xl font-bold"
          data-i18n="travelerPicturesTitle"
        >
          {{ $t('travelerPicturesTitle') }}
        </div>
        <button
          class="btn btn-lightest-blue text-blue font-bold rounded-[1.25rem] ml-auto px-4"
          data-i18n="seeAll"
          @click="openGallery('gallery')"
        >
          <span>
            {{ $t('seeAll') }}
          </span>
          <span class="pl-2">
            <fa icon="chevron-right" />
          </span>
        </button>
      </div>
      <div
        v-if="isWider('md')"
        class="thumbnail-grid"
      >
        <div
          v-for="(picture, index) in travelerPictures?.slice(0, 7)"
          :key="index"
          class="thumbnail"
        >
          <img
            :src="picture.thumbnail"
            :alt="`Thumbnail ${index + 1}`"
            @click="openGallery(index)"
          />
        </div>
      </div>
      <div
        v-else
        class="thumbnail-grid"
        :class="{ 'three-images': images.length === 3 }"
      >
        <div
          v-for="(picture, index) in travelerPictures?.slice(0, 4)"
          :key="index"
          class="thumbnail"
          @click="openGallery(index)"
        >
          <img
            :src="picture.thumbnail"
            :alt="`Thumbnail ${index + 1}`"
          />
        </div>
      </div>
    </ScreenWidthProvider>
    <FullscreenImageCarousel
      v-if="showGallery"
      :key="hgKey"
      :images="images"
      :initial-slide="initialSlide"
      :visible="showGallery"
      @on-hide="showGallery = false"
    />
  </div>
</template>

<script setup>
const tripStore = useTripStore()

const showGallery = ref(false)
const hgKey = ref(1)
const initialSlide = ref(0)

const props = defineProps({
  tripTypeId: {
    type: [String, Number],
    required: true,
  },

  size: {
    type: String,
    default: '200x200',
  },

  /**
   * The fit mode for the thumbnail images.
   * @type {string}
   * @default ''
   * @example "": 'inside', // keep ratio, one side may be smaller then requested
   * @example c: 'cover', // keep ratio, match both width and height, cut of overflow
   * @example o: 'outside', // keep ratio, one side may be larger then requested
   * @example f: 'fill', // ignore ratio, match both width and height, stretch
   */
  fit: {
    type: String,
    default: '',
  },
})

const { data: travelerPictures } = useAsyncData(async () => {
  const result = await tripStore.fetchTravelerPicturesByTriptype(props.tripTypeId, props.size, props.fit)

  return result || []
})

const images = computed(() => {
  return travelerPictures.value?.map(picture => picture.uri) || []
})

const imagesCount = computed(() => {
  return Math.min(images.value?.length, 7)
})

const imagesCountMobile = computed(() => {
  return images.value?.length <= 2 ? 1 : 2
})

const openGallery = (index) => {
  if (index === 'gallery') {
    initialSlide.value = 0
    showGallery.value = true
    return
  }
  initialSlide.value = index
  showGallery.value = true
}
</script>

<style lang="scss" scoped>
.btn {
  text-transform: none !important;
  width: 120px;
  height: 100%;
}
.thumbnail-grid {
  display: grid;
  grid-template-columns: repeat(v-bind(imagesCount), 1fr);
  gap: 10px;
}

.thumbnail img {
  max-height: 250px;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  cursor: pointer;
}

@media (max-width: 768px) {
  .thumbnail-grid {
    grid-template-columns: repeat(v-bind(imagesCountMobile), 1fr);
  }

  .thumbnail-grid.three-images {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }

  .thumbnail-grid.three-images .thumbnail:nth-child(3) {
    grid-column: span 2;
  }
}
</style>